<template>
    <section>
        <VCard>
            <VCardText class="d-flex flex-wrap gap-4">
                <VSpacer />

                <div class="d-flex align-center flex-wrap gap-4">
                    <!-- 👉 Search  -->
                    <AppTextField v-model="searchQuery" :placeholder="t('admin_notifications.search_by_name')"
                        density="compact" prepend-inner-icon="tabler-search" style="inline-size: 20rem;" clearable />

                    <!-- 👉 Add service button -->
                    <VBtn v-if="$can('create', 'service')" color="success" prepend-icon="tabler-plus"
                        @click="openServiceModal()">
                        {{ $t('admin_notifications.add_new') }}
                    </VBtn>
                </div>
            </VCardText>

            <VDivider />

            <!-- SECTION datatable -->
            <VDataTableServer v-model:items-per-page="itemsPerPage" v-model:page="page" :items-per-page-options="[
                { value: 10, title: '10' },
                { value: 20, title: '20' },
                { value: 50, title: '50' },
                { value: -1, title: '$vuetify.dataFooter.itemsPerPageAll' },
            ]" :items="services" :items-length="totalServices" :headers="headers" class="text-no-wrap styled-table"
                @update:options="updateOptions">
                <!-- Service Name -->
                <template #item.name="{ item }">
                    <span class="font-weight-medium">{{ item.name }}</span>
                </template>

                <!-- Actions -->
                <template #item.actions="{ item }">
                    <div class="d-flex gap-2">
                        <IconBtn v-if="$can('edit', 'service')" @click="openServiceModal(item)">
                            <VIcon icon="tabler-edit" />
                        </IconBtn>
                        <!-- <IconBtn v-if="$can('delete', 'service')" color="error" @click="deleteService(item)">
                            <VIcon icon="tabler-trash" />
                        </IconBtn> -->
                    </div>
                </template>


                <template #bottom>
                    <VCardText class="pt-2">
                        <div class="d-flex flex-wrap justify-center justify-sm-space-between gap-y-2 mt-2">
                            <VSelect v-model="itemsPerPage" :items="[10, 25, 50, 100]" label="Rows per page:"
                                variant="underlined" style="max-inline-size: 8rem;min-inline-size: 5rem;" />

                            <VPagination v-model="page" :total-visible="$vuetify.display.smAndDown ? 3 : 5"
                                :length="Math.ceil(totalServices / itemsPerPage)" />
                        </div>
                    </VCardText>
                </template>
            </VDataTableServer>
        </VCard>

        <!-- Service Modal -->
        <VDialog v-model="isServiceModalVisible" max-width="500px">
            <VCard>
                <VCardTitle>
                    {{ selectedService ? t('admin_notifications.edit_service') :
                        t('admin_notifications.add_new_service') }}
                </VCardTitle>

                <VCardText>
                    <!-- Error Alert -->
                    <VAlert v-if="Object.keys(errors).length > 0" icon="tabler-alert-circle" type="error"
                        variant="tonal" class="mb-4" closable @click:close="errors = {}">
                        <template #title>
                            {{ errors.general ? t('admin_notifications.error') :
                                t('admin_notifications.validation_errors') }}
                        </template>

                        <ul class="mb-0">
                            <li v-for="(error, field) in errors" :key="field">
                                <span v-if="field === 'general'">{{ error }}</span>
                                <span v-else><strong>{{ field }}:</strong> {{ error }}</span>
                            </li>
                        </ul>
                    </VAlert>

                    <VForm ref="formRef" @submit.prevent="onSubmit">
                        <VRow>
                            <VCol cols="12">
                                <VTextField v-model="form.name" :label="t('admin_notifications.service_name')"
                                    :placeholder="t('admin_notifications.enter_service_name')"
                                    :rules="[v => !!v || t('admin_notifications.name_required')]"
                                    :error-messages="errors.name" required />
                            </VCol>
                        </VRow>
                    </VForm>
                </VCardText>

                <VCardActions class="pa-4 d-flex gap-4 justify-end">
                    <VBtn style="width: 150px;" variant="flat" @click="onSubmit" :loading="isSubmitting">
                        {{ $t('admin_notifications.submit') }}
                    </VBtn>
                    <VBtn color="secondary" variant="tonal" style="width: 150px;" @click="closeServiceModal">
                        {{ $t('admin_notifications.cancel') }}
                    </VBtn>
                </VCardActions>
            </VCard>
        </VDialog>
    </section>
</template>

<script setup>

definePage({
    meta: {
        action: 'view',
        subject: 'service',
    },
})

import { VDataTableServer } from 'vuetify/labs/VDataTable'
import { paginationMeta } from '@api-utils/paginationMeta'
import $api from "~/utils/api";
import { onMounted, ref, watch, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue'

const { t } = useI18n()

// Set page title
useHead({
    title: t('menu.services'),
    meta: [
        { name: 'description', content: t('admin_notifications.services_description') }
    ]
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

// Search
const searchQuery = ref('')
const debouncedSearchQuery = ref('')
let searchTimeout = null

const debounceSearch = (value) => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        debouncedSearchQuery.value = value
    }, 500)
}

const updateOptions = options => {
    page.value = options.page
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
    {
        title: '#',
        key: 'id',
        sortable: true,
        width: '80px',
    },
    {
        title: 'SERVICE NAME',
        key: 'name',
        sortable: true,
    },
    {
        title: 'ACTIONS',
        key: 'actions',
        sortable: false,
        width: '120px',
    },
]

const {
    data: servicesData,
    execute: fetchServices,
} = await useApi(createUrl('/api/services', {
    query: {
        q: debouncedSearchQuery,
        per_page: itemsPerPage,
        page,
        sortBy,
        orderBy,
    },
}))

const services = computed(() => servicesData.value?.data || [])
const totalServices = computed(() => servicesData.value?.meta?.total || 0)

// Modal state
const isServiceModalVisible = ref(false)
const selectedService = ref(null)
const isSubmitting = ref(false)
const formRef = ref()
const errors = ref({})

// Form data
const form = ref({
    name: '',
})

// Watch for filter changes
watch([debouncedSearchQuery, itemsPerPage], () => {
    page.value = 1
    fetchServices()
})

// Watch for search query changes to apply debounce
watch(searchQuery, (newValue) => {
    debounceSearch(newValue)
})

// Modal methods
const openServiceModal = (service = null) => {
    selectedService.value = service
    if (service) {
        form.value = { ...service }
    } else {
        form.value = {
            name: '',
        }
    }
    isServiceModalVisible.value = true
}

const closeServiceModal = () => {
    isServiceModalVisible.value = false
    selectedService.value = null
    form.value = {
        name: '',
    }
    errors.value = {} // Очищаем ошибки
}

const onSubmit = async () => {
    const { valid } = await formRef.value.validate()
    if (!valid) return

    isSubmitting.value = true
    errors.value = {} // Очищаем предыдущие ошибки

    try {
        if (selectedService.value) {
            // Update
            await $api.post(`/services/${selectedService.value.id}`, form.value)
        } else {
            // Create
            await $api.post('/services', form.value)
        }

        closeServiceModal()
        await fetchServices()
    } catch (error) {
        console.error('Ошибка при сохранении услуги:', error)

        // Обрабатываем ошибки валидации с сервера
        if (error.response?.data?.errors) {
            errors.value = error.response.data.errors
        } else if (error.response?.data?.message) {
            // Общая ошибка
            errors.value = { general: error.response.data.message }
        } else {
            // Неизвестная ошибка
            errors.value = { general: 'Произошла неизвестная ошибка' }
        }
    } finally {
        isSubmitting.value = false
    }
}

// Delete service
const deleteService = async (service) => {
    if (!confirm(t('admin_notifications.confirm_delete_service', { name: service.name }))) return

    try {
        await $api.delete(`/services/${service.id}`)
        await fetchServices()
    } catch (error) {
        console.error('Ошибка при удалении услуги:', error)
    }
}

// Load services on mount
onMounted(() => {
    fetchServices()
})
</script>



<style scoped>
.styled-table :deep(.v-data-table__th) {
    font-weight: bold !important;
    position: relative;
}

.styled-table :deep(.v-data-table__th:not(:last-child))::after {
    content: '';
    position: absolute;
    right: 0;
    top: 30%;
    bottom: 30%;
    width: 2px;
    background-color: rgba(var(--v-theme-on-surface), 0.12);
}

.styled-table :deep(.v-data-table-header__content) {
    font-weight: bold !important;
}
</style>
